import React from 'react';

import { AGGREGATE_NAMES, LOAN_REQUEST_COMMANDS, type LOAN_REQUEST_OFFER_SOURCE } from 'cpool-constants';
import { useTranslation } from 'react-i18next';

import { useOfferCommissionMutation } from '../../hooks/useOfferCommissionMutation';
import { InfoDropdown } from '~/components/Dropdown/InfoDropdown';
import CurrencyInput from '~/features/leadDetails/components/Overview/CustomerData/General/CurrencyInput';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { calculateCommission } from '~/features/leadDetails/utils/calculateCommission';
import { currencyFormat } from '~/lib/i18n';

type Props = {
    source: LOAN_REQUEST_OFFER_SOURCE;
    id: string;
    leadId: string;
    receivedCommission?: number | null;
    responseCommissionInPercent: number | null;
    kreditbetrag?: number | null;
};

export const Commission: React.FC<Props> = ({
    id,
    kreditbetrag,
    leadId,
    receivedCommission,
    responseCommissionInPercent,
    source,
}) => {
    const [commission, setCommission] = React.useState<number>(receivedCommission ?? 0);
    const { t } = useTranslation();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const queryMutator = useOfferCommissionMutation({ id, leadId, source });
    const canChangeCommission =
        permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST][LOAN_REQUEST_COMMANDS.changeOfferCommission];
    const responseCommission = calculateCommission({
        kreditbetrag,
        provision: responseCommissionInPercent,
    });

    const handleBlur = async (_event: React.FocusEvent<HTMLInputElement>) => {
        const isDifferent = receivedCommission !== commission;
        const shouldMutate = isDifferent && !(commission === 0 && receivedCommission === null);
        if (shouldMutate) {
            queryMutator.mutate(commission);
        }
    };

    return (
        <td
            onClick={(e) => e.stopPropagation()}
            className="text-10 text-center text-grey whitespace-nowrap p-1 max-w-[9rem]"
        >
            <CurrencyInput
                disabled={!canChangeCommission}
                value={commission}
                setValue={setCommission}
                onBlur={handleBlur}
            />
            <InfoDropdown
                title={t('OFFER_MANAGEMENT.COMMISSION.INFO_TITLE')}
                content={[
                    `${t('OFFER_MANAGEMENT.COMMISSION.RESPONSE_INFO', {
                        commission: responseCommission
                            ? currencyFormat(responseCommission)
                            : t('OFFER_MANAGEMENT.COMMISSION.RESPONSE_INFO_NOT_YET_AVAILABLE'),
                    })}`,
                ]}
            />
        </td>
    );
};
